/**
 * Sprint BG Part 2 — GA4 (gtag.js) stub (apps/user)。
 *
 * 状態: **configuration foundation only**。Measurement ID (`VITE_GA4_MEASUREMENT_ID`) が
 * 空 or 未設定なら **silent no-op**。実 dep は不要 (script tag inject 方式)。
 *
 * 仕組み:
 *   - `initGa4()` を main.tsx から 1 回呼ぶと、Google が提供する
 *     `https://www.googletagmanager.com/gtag/js?id=<ID>` を head に追加し、
 *     `window.gtag` を defining して config を送信する
 *   - 既に init 済 / ID 未設定なら no-op
 *   - `trackPageView(path)` / `trackEvent(name, params)` を helper として提供
 *
 * Note:
 *   - CitizenWalk は匿名 auth のため、user_id / IP は明示的に anonymize_ip='true' を渡す
 *   - cookie_flags='SameSite=None;Secure' で iOS PWA からも乗る
 *
 * 参照: docs/14 観測戦略 §4 (GA4 / docs/13 §3 PII 最小化方針)
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const MEASUREMENT_ID: string = import.meta.env.VITE_GA4_MEASUREMENT_ID ?? '';

let initialized = false;

/**
 * GA4 gtag.js を head に注入し、`window.gtag` を defining して config を送信する。
 *
 * 既に init 済 or ID 未設定 / SSR 環境なら no-op。
 */
export function initGa4(): void {
  if (initialized) return;
  if (!MEASUREMENT_ID) {
    // eslint-disable-next-line no-console
    console.info(
      '[GA4] Measurement ID 未設定のためスキップ (VITE_GA4_MEASUREMENT_ID を .env.local に設定すると有効化)。',
    );
    return;
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // 1. dataLayer / gtag stub を先に定義 (script load 前の呼び出しを queue)
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag(...args: unknown[]): void {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
    // SPA は手動で page_view を送る (旧 OutboxList / DistrictHeader でも router 連携を想定)
    send_page_view: false,
  });

  // 2. 実 script を非同期 inject
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);

  initialized = true;
  // eslint-disable-next-line no-console
  console.info('[GA4] initialized', { measurementId: MEASUREMENT_ID });
}

/**
 * SPA route 遷移時の page_view を送信。Router の listener から呼ぶ。
 */
export function trackPageView(path: string): void {
  if (!initialized || !window.gtag) return;
  window.gtag('event', 'page_view', { page_path: path });
}

/**
 * 任意の event を送信。`post.created` / `agree.toggled` 等の domain event を想定。
 */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!initialized || !window.gtag) return;
  window.gtag('event', name, params ?? {});
}
